import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { getTerms, termsSpeciesKeyFromSlug } from "@/lib/speciesTerms";
import { SERVICE_CATALOG } from "@/lib/serviceCatalog";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const BrowseServiceFind = ({ kind }) => {
  const location = useLocation();
  const species = location.pathname.split('/')[2]; // /browse/:species/.../find
  const query = useQuery();

  const role = query.get('role');
  const type = query.get('type');

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    state: '',
    city: ''
  });

  const title = (() => {
    if (kind === 'breeding') {
      const key = termsSpeciesKeyFromSlug(species);
      const { male, female } = getTerms(key);
      const term = role === 'female' ? female : male;
      return `Find a ${term}`;
    }

    const svc = SERVICE_CATALOG.find((s) => s.type === type);
    return svc ? `Find a ${svc.title}` : 'Find';
  })();

  // Fetch providers
  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          kind,
          species
        };

        if (kind === 'service' && type) {
          params.service_type = type;
        }

        if (kind === 'breeding' && role) {
          params.role = role;
        }

        if (filters.state) {
          params.state = filters.state;
        }

        if (filters.city) {
          params.city = filters.city;
        }

        const response = await api.get('/service-providers/search', { params });
        setProviders(response.data.providers || []);
      } catch (err) {
        logger.error('Failed to fetch providers:', err);
        setError('Failed to load service providers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [kind, species, type, role, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ state: '', city: '' });
  };

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Service Directory</p>
        <h1 className="text-4xl text-[#EDEDED] mb-4">{title}</h1>
        <p className="text-[#A1A1AA] mb-8">Browse verified service providers in your area.</p>

        {/* Filters */}
        <div className="glass-card p-6 mb-8">
          <p className="text-sm text-[#A1A1AA] mb-4">Filter Results</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-[#71717A] mb-2">State</label>
              <input
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
                className="input-minimal"
                placeholder="e.g., CA, TX, NY"
              />
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-2">City</label>
              <input
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                className="input-minimal"
                placeholder="City name"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[#A1A1AA] mt-4">Loading providers...</p>
          </div>
        ) : error ? (
          <div className="glass-card p-6 text-center">
            <p className="text-[#EF4444]">{error}</p>
          </div>
        ) : providers.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-[#A1A1AA] mb-4">No service providers found matching your criteria.</p>
            <p className="text-sm text-[#71717A] mb-6">
              Try adjusting your filters or check back later as more providers join.
            </p>
            <Link 
              to={`/browse/${species}/${kind === 'service' ? 'services' : 'breeding'}/register${kind === 'service' ? `?type=${type}` : `?role=${role}`}`}
              className="btn-gold text-sm"
            >
              Register as a Provider
            </Link>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <p className="text-sm text-[#A1A1AA]">
                Found {providers.length} provider{providers.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map((provider) => (
                <div key={provider.provider_id} className="glass-card p-6 hover:border-[#D4AF37] transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg text-[#EDEDED] font-medium">{provider.display_name}</h3>
                      {provider.is_verified && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#D4AF37] mt-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </span>
                      )}
                    </div>
                  </div>

                  {provider.description && (
                    <p className="text-sm text-[#A1A1AA] mb-4 line-clamp-2">
                      {provider.description}
                    </p>
                  )}

                  <div className="space-y-2 text-sm">
                    {provider.location_city && provider.location_state && (
                      <div className="flex items-center gap-2 text-[#71717A]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{provider.location_city}, {provider.location_state}</span>
                      </div>
                    )}

                    {provider.phone && (
                      <div className="flex items-center gap-2 text-[#71717A]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{provider.phone}</span>
                      </div>
                    )}

                    {provider.website && (
                      <div className="flex items-center gap-2 text-[#71717A]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">
                          Website
                        </a>
                      </div>
                    )}

                    {provider.email && (
                      <div className="flex items-center gap-2 text-[#71717A]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <a href={`mailto:${provider.email}`} className="text-[#D4AF37] hover:underline truncate">
                          {provider.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Back Button */}
        <div className="mt-12 text-center">
          <Link to="/" className="btn-secondary text-sm">
            Back to Breedling Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BrowseServiceFind;
